import { defineStore } from "pinia"

import { useUserSessionStore } from "@/store/userSession.js"

import * as texts from "@/services/texts.js"

import { MOBILE_BOTTOM_BAR_BUTTONS_AREA_HEIGHT, getMobileBottomBarPadding } from "@/constants/barHeights.js"
import { audioProfiles } from "@/constants/livestreamProviders.js"
import { cookiesRestrictionLevels } from "@/constants/cookiesRestrictions.js"

const VALID_REFERRAL_CODES_STORAGE_LIMIT = 10

export const useAppStateStore = defineStore("appState", {
  state: () => ({
    enablePushNotificationsState: "Notification" in window && Notification?.permission === "granted",
    localAvatarUrlState: null,
    searchOpenUUIDState: null,
    postsBeingProcessedState: [],
    moreMenuDisplayedState: false,
    inboxDisplayedState: false,
    lastPushNotificationsAnalyticsUpdateState: null,
    disabledPushNotificationPromptSkipDateState: null,
    desktopPaywallNudgeBannerPromptSkipDateState: null,
    desktopInviteBannerSkipDateState: null,
    installPopupDisabledState: false,
    validReferralCodesUsedState: [],
    authPageEmailValueState: null,
    redirectingAfterLogInState: false,
    cookiesBannerAcceptedState: false,
    cookiesRestrictionLevelState: cookiesRestrictionLevels.allow_essentials,
    cookiesRestrictionLevelDialogDismissedState: false,
    lastPaywallSourceState: null,
    webViewModeState: false,
    onboardingRedirectionState: false,
    walkthroughSkippedState: false,
    subscriptionIdInProgressState: null,
    audioProfileState: audioProfiles.musicStandard
  }),
  getters: {
    localAvatarUrl: (state) => state.localAvatarUrlState,
    enablePushNotifications: (state) => state.enablePushNotificationsState,
    searchOpenUUID: (state) => state.searchOpenUUIDState,
    postsBeingProcessed: (state) => state.postsBeingProcessedState,
    moreMenuDisplayed: (state) => state.moreMenuDisplayedState,
    inboxDisplayed: (state) => state.inboxDisplayedState,
    lastPushNotificationsAnalyticsUpdate: (state) => state.lastPushNotificationsAnalyticsUpdateState,
    disabledPushNotificationPromptSkipDate: (state) => state.disabledPushNotificationPromptSkipDateState,
    desktopPaywallNudgeBannerPromptSkipDate: (state) => state.desktopPaywallNudgeBannerPromptSkipDateState,
    desktopInviteBannerSkipDate: (state) => state.desktopInviteBannerSkipDateState,
    installPopupDisabled: (state) => state.installPopupDisabledState,
    validReferralCodesUsed: (state) => state.validReferralCodesUsedState,
    mobileBottomBarPadding: () => getMobileBottomBarPadding(),
    mobileBottomBarHeight() {
      return MOBILE_BOTTOM_BAR_BUTTONS_AREA_HEIGHT + this.mobileBottomBarPadding
    },
    authPageEmailValue: (state) => state.authPageEmailValueState,
    redirectingAfterLogIn: (state) => state.redirectingAfterLogInState,
    cookiesRestrictionLevel: (state) => state.cookiesRestrictionLevelState,
    cookiesBannerAccepted: (state) => state.cookiesBannerAcceptedState,
    cookiesRestrictionLevelDialogDismissed: (state) => state.cookiesRestrictionLevelDialogDismissedState,
    lastPaywallSource: (state) => state.lastPaywallSourceState,
    webViewMode: (state) => state.webViewModeState,
    onboardingRedirection: (state) => state.onboardingRedirectionState,
    walkthroughSkipped: (state) => state.walkthroughSkippedState,
    subscriptionIdInProgress: (state) => state.subscriptionIdInProgressState,
    audioProfile: (state) => state.audioProfileState,
    onboardingModeOrRedirection: (state) => {
      const userSessionStore = useUserSessionStore()
      return (userSessionStore.user && !userSessionStore.user.onboarding_completed) || state.onboardingRedirectionState
    }
  },
  actions: {
    clearState() {
      this.setPostsBeingProcessed([])
      this.setMoreMenuDisplayed(false)
      this.setLastPushNotificationsAnalyticsUpdate(null)
      this.clearValidReferralCodes()
      this.setAuthPageEmailValue(null)
      this.setSubscriptionIdInProgress(null)
    },
    setPushNotifications(value) {
      this.enablePushNotificationsState = value
    },
    setLocalAvatarUrl(avatar) {
      if (avatar === null) {
        window.URL.revokeObjectURL(this.localAvatarUrlState)
      }
      this.localAvatarUrlState = avatar
    },
    refreshSearchOpenUUID() {
      this.searchOpenUUIDState = texts.generateUUID()
    },
    setPostsBeingProcessed(posts) {
      this.postsBeingProcessedState = posts
    },
    setMoreMenuDisplayed(value) {
      this.moreMenuDisplayedState = value
    },
    setInboxDisplayed(value) {
      this.inboxDisplayedState = value
    },
    setLastPushNotificationsAnalyticsUpdate(enabled) {
      this.lastPushNotificationsAnalyticsUpdateState = enabled
    },
    setDisabledPushNotificationPromptSkipDate() {
      this.disabledPushNotificationPromptSkipDateState = new Date().toISOString()
    },
    setDesktopPaywallNudgeBannerPromptSkipDate() {
      this.desktopPaywallNudgeBannerPromptSkipDateState = new Date().toISOString()
    },
    setDesktopInviteBannerSkipDate() {
      this.desktopInviteBannerSkipDateState = new Date().toISOString()
    },
    setInstallPopupDisabled() {
      this.installPopupDisabledState = true
    },
    addValidReferralCode(item) {
      this.validReferralCodesUsedState.push(item)
      if (this.validReferralCodesUsedState.length > VALID_REFERRAL_CODES_STORAGE_LIMIT) {
        this.validReferralCodesUsedState.shift()
      }
    },
    clearValidReferralCodes() {
      this.validReferralCodesUsedState = []
    },
    setAuthPageEmailValue(item) {
      this.authPageEmailValueState = item
    },
    setRedirectingAfterLogin(item) {
      this.redirectingAfterLogInState = item
    },
    setCookiesBannerAccepted() {
      this.cookiesBannerAcceptedState = true
    },
    setCookiesRestrictionLevel(mode) {
      this.cookiesRestrictionLevelState = mode
    },
    setCookiesRestrictionLevelDialogDismissed() {
      this.cookiesRestrictionLevelDialogDismissedState = true
    },
    setLastPaywallSource(item) {
      this.lastPaywallSourceState = item
    },
    setWebViewMode() {
      this.webViewModeState = true
    },
    setOnboardingRedirection(item) {
      this.onboardingRedirectionState = item
    },
    setWalkthroughSkipped() {
      this.walkthroughSkippedState = true
    },
    setSubscriptionIdInProgress(item) {
      this.subscriptionIdInProgressState = item
    },
    setAudioProfile(item) {
      this.audioProfileState = item
    }
  },
  persist: {
    pick: [
      "cookiesBannerAcceptedState",
      "cookiesRestrictionLevelState",
      "cookiesRestrictionLevelDialogDismissedState",
      "desktopPaywallNudgeBannerPromptSkipDateState",
      "desktopInviteBannerSkipDateState",
      "disabledPushNotificationPromptSkipDateState",
      "enablePushNotificationsState",
      "installPopupDisabledState",
      "lastPushNotificationsAnalyticsUpdateState",
      "lastPaywallSourceState",
      "postsBeingProcessedState",
      "validReferralCodesUsedState",
      "subscriptionIdInProgressState",
      "audioProfileState"
    ]
  }
})
